<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.taskSummary') }}</b-card-title>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="selectedFilter"
          :options="filterOptions"
          size="sm"
          class="mr-1"
          @change="onFilterChange"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          :disabled="isLoading"
          @click="refreshData"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :class="{ 'animate-spin': isLoading }"
          />
        </b-button>
      </div>
    </b-card-header>
    <b-card-body v-if="tasks">
      <b-row>
        <!-- Task Statistics -->
        <b-col
          lg="3"
          md="6"
          class="mb-2"
        >
          <b-card
            bg-variant="primary"
            text-variant="white"
            class="text-center"
          >
            <h3 class="text-white mb-1">
              {{ tasks.total_tasks }}
            </h3>
            <span>{{ $t('dashboard.totalTasks') }}</span>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          class="mb-2"
        >
          <b-card
            bg-variant="danger"
            text-variant="white"
            class="text-center"
          >
            <h3 class="text-white mb-1">
              {{ tasks.overdue_count }}
            </h3>
            <span>{{ $t('dashboard.overdueTasks') }}</span>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          class="mb-2"
        >
          <b-card
            bg-variant="warning"
            text-variant="white"
            class="text-center"
          >
            <h3 class="text-white mb-1">
              {{ tasks.due_today }}
            </h3>
            <span>{{ $t('dashboard.dueToday') }}</span>
          </b-card>
        </b-col>

        <b-col
          lg="3"
          md="6"
          class="mb-2"
        >
          <b-card
            bg-variant="info"
            text-variant="white"
            class="text-center"
          >
            <h3 class="text-white mb-1">
              {{ tasks.due_this_week }}
            </h3>
            <span>{{ $t('dashboard.dueThisWeek') }}</span>
          </b-card>
        </b-col>

        <!-- Completion Rate -->
        <b-col
          cols="12"
          class="mb-2"
        >
          <div class="d-flex justify-content-between align-items-center mb-1">
            <span>{{ $t('dashboard.completionRate') }}</span>
            <strong>{{ tasks.completion_rate }}%</strong>
          </div>
          <b-progress
            :value="tasks.completion_rate"
            :variant="getCompletionVariant(tasks.completion_rate)"
            height="20px"
            show-value
          />
        </b-col>

        <!-- Status Distribution -->
        <b-col
          lg="6"
          md="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.byStatus') }}
          </h5>
          <vue-apex-charts
            type="bar"
            height="300"
            :options="statusChartOptions"
            :series="statusChartSeries"
          />
        </b-col>

        <!-- Priority Distribution -->
        <b-col
          lg="6"
          md="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.byPriority') }}
          </h5>
          <vue-apex-charts
            type="donut"
            height="300"
            :options="priorityChartOptions"
            :series="priorityChartSeries"
          />
        </b-col>

        <!-- Upcoming Tasks -->
        <b-col
          cols="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.upcomingTasks') }}
          </h5>
          <b-table
            :items="tasks.upcoming_tasks"
            :fields="upcomingFields"
            responsive
            class="mb-0"
            small
            striped
            hover
          >
            <template #cell(name)="data">
              <b-link>
                {{ data.value }}
              </b-link>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.value)">
                {{ $t(`task.status.${data.value}`) }}
              </b-badge>
            </template>
            <template #cell(priority)="data">
              <b-badge :variant="getPriorityVariant(data.value)">
                {{ $t(`task.priority.${data.value}`) }}
              </b-badge>
            </template>
            <template #cell(due_date)="data">
              {{ formatDate(data.value) }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <p class="text-muted">
          {{ $t('dashboard.noDataAvailable') }}
        </p>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BTable,
  BBadge,
  BLink,
  BSpinner,
  BProgress,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'TaskSummaryWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormSelect,
    BTable,
    BBadge,
    BLink,
    BSpinner,
    BProgress,
    VueApexCharts,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      selectedFilter: 'assigned',
      filterOptions: [
        { value: 'assigned', text: this.$t('dashboard.filter.assigned') },
        { value: 'all', text: this.$t('dashboard.filter.all') },
        { value: 'my_projects', text: this.$t('dashboard.filter.myProjects') },
      ],
      upcomingFields: [
        { key: 'name', label: this.$t('task.name') },
        { key: 'status', label: this.$t('task.status') },
        { key: 'priority', label: this.$t('task.priority') },
        { key: 'project_name', label: this.$t('task.project') },
        { key: 'assigned_to', label: this.$t('task.assignedTo') },
        { key: 'due_date', label: this.$t('task.dueDate') },
      ],
    }
  },
  computed: {
    ...mapGetters('dashboard', ['tasks', 'loading', 'filters']),
    isLoading() {
      return this.loading.tasks
    },
    statusChartSeries() {
      if (!this.tasks || !this.tasks.by_status) return []
      return [
        {
          name: this.$t('dashboard.tasks'),
          data: Object.values(this.tasks.by_status),
        },
      ]
    },
    statusChartOptions() {
      if (!this.tasks || !this.tasks.by_status) return {}
      return {
        chart: {
          type: 'bar',
        },
        xaxis: {
          categories: Object.keys(this.tasks.by_status).map(key => this.$t(`task.status.${key}`)),
        },
        colors: ['#3b82f6'],
        plotOptions: {
          bar: {
            borderRadius: 8,
            horizontal: false,
          },
        },
      }
    },
    priorityChartSeries() {
      if (!this.tasks || !this.tasks.by_priority) return []
      return Object.values(this.tasks.by_priority)
    },
    priorityChartOptions() {
      if (!this.tasks || !this.tasks.by_priority) return {}
      return {
        labels: Object.keys(this.tasks.by_priority).map(key => this.$t(`task.priority.${key}`)),
        colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'],
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
            },
          },
        },
      }
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchTasks', 'refreshWidget']),
    onFilterChange() {
      this.fetchTasks(this.selectedFilter)
    },
    refreshData() {
      this.refreshWidget({ widget: 'tasks', params: { filter: this.selectedFilter } })
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'primary',
        completed: 'success',
        blocked: 'danger',
        client_pending: 'info',
      }
      return variants[status] || 'secondary'
    },
    getPriorityVariant(priority) {
      const variants = {
        low: 'success',
        medium: 'info',
        high: 'warning',
        urgent: 'danger',
      }
      return variants[priority] || 'secondary'
    },
    getCompletionVariant(percentage) {
      if (percentage < 50) return 'danger'
      if (percentage < 75) return 'warning'
      return 'success'
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('ar-EG')
    },
  },
  created() {
    this.selectedFilter = this.filters.tasksFilter || 'assigned'
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
